import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import PropTypes from "prop-types";
import styles from "./E3Selectbox.module.css";
import { getChildrenCommonCode } from "e3-common/utils/commonCodeUtil";
import useFetch from "e3-common/hooks/useFetch";
import _ from "lodash";

/**
 * E3Selectbox
 * @type React.FC<E3SelectboxPropTypes>
 */
const E3Selectbox = React.forwardRef((props, ref) => {
  const { request } = useFetch();
  const refSelect = useRef();
  const callbackRef = useRef();

  const [currState, setCurrState] = useState({
    selectedIndex: 0,
    selectedValue: "",
    selectedData: null,
    placeholder: props.placeholder,
    isSearchable: props.isSearchable,
    isClearable: props.isClearable,
    disabled: props.disabled,
    dataSource: [],
    options: [],
  });

  Object.keys(props).forEach((p) => {
    if (!Object.keys(E3SelectboxPropTypes).includes(p)) {
      console.error(`E3Selectbox: 정의되지 않은 prop ${p}가 전달되었습니다.`);
    }
  });

  /** 외부에서 접근할 수 있는 함수들 */
  React.useImperativeHandle(ref, () => ({
    /**
     * value(값) 리턴
     * @returns
     */
    getValue() {
      const data = currState.selectedValue;
      if (!data || Object.keys(data).length === 0) return "";

      if (props.isMulti) {
        // 배열인 경우 아닌경우로 나눔
        if (Array.isArray(data)) {
          return data.map((x) => x.value).join(",");
        } else {
          return data?.["value"] ?? "";
        }
        // 기존 소스
        // if (data?.["value"]) { return data?.["value"]; } else { return data?.map((x) => x.value).join(","); }
      } else {
        return data?.["value"] ? data?.["value"] : "";
      }
    },
    /**
     * value(값) 세팅
     * @param {*} v
     */
    setValue(v) {
      const { dataSource } = currState;

      // 멀티 셀렉트인 경우 쉼표로 구분된 문자열을 배열로 처리
      if (props.isMulti && typeof v === "string" && v.includes(",")) {
        const values = v
          .split(",")
          .map((val) => val.trim())
          .filter((val) => val);
        const selectedItems = values
          .map((val) => {
            const idx = dataSource?.findIndex((x) => x[props.valueId] === val);
            return idx !== -1 ? dataSource[idx] : null;
          })
          .filter((item) => item !== null);

        const selectedValues = selectedItems.map((item) => _keyValueForm(item));
        _setValue(selectedValues);
        return;
      }

      // 멀티 셀렉트인 경우 배열 처리
      if (props.isMulti && Array.isArray(v)) {
        const selectedItems = v
          .map((val) => {
            const idx = dataSource?.findIndex((x) => x[props.valueId] === val);
            return idx !== -1 ? dataSource[idx] : null;
          })
          .filter((item) => item !== null);

        const selectedValues = selectedItems.map((item) => _keyValueForm(item));
        _setValue(selectedValues);
        return;
      }

      // 단일 셀렉트 처리 (기존 로직)
      const selectedIdx =
        dataSource?.length > 0
          ? dataSource?.findIndex((x, idx) => {
              return x[props.valueId] === v || idx === Number(v);
            })
          : null;

      const selectedData = selectedIdx != null && dataSource[selectedIdx];
      const selectedValue = _keyValueForm(selectedData);

      _setValue(selectedValue);
    },
    /**
     * 쉼표로 구분된 문자열 값을 멀티 셀렉트에 설정 (명시적 함수)
     * @param {string} commaSeparatedValues - 쉼표로 구분된 값들 (예: "VALUE1,VALUE2,VALUE3")
     */
    setValueFromCommaSeparated(commaSeparatedValues) {
      if (!props.isMulti) {
        console.warn(
          "E3Selectbox: setValueFromCommaSeparated는 멀티 셀렉트에서만 사용 가능합니다."
        );
        return;
      }

      if (typeof commaSeparatedValues !== "string") {
        console.warn(
          "E3Selectbox: setValueFromCommaSeparated의 매개변수는 문자열이어야 합니다."
        );
        return;
      }

      this.setValue(commaSeparatedValues);
    },
    /**
     * 선택된 값에 대해 내부적인 값 포함 리턴
     * @returns
     */
    getSelectedData() {
      const { selectedData } = currState;
      return selectedData;
    },

    getDataSource() {
      const { dataSource } = currState;
      return dataSource;
    },

    /**
     * url options setting
     * @param {*} url
     * @param {*} method
     * @param {*} body
     */
    setDataBindingByAPI(url, method, body, _setVal = "", _callback) {
      _getDataFromAPIAndSet(url, method, body, _setVal, _callback);
    },
    /**
     * 첫번째 값 또는 기본 값으로 되돌리기
     */
    clear() {
      const { dataSource } = currState;
      const idx = props.defaultValue
        ? dataSource?.findIndex((x, idx) => {
            return (
              x[props.valueId] === props.defaultValue ||
              idx === Number(props.defaultValue)
            );
          })
        : 0;
      const firstRow = dataSource.length > 0 ? dataSource[idx] : null;
      const firstRowValue = _keyValueForm(firstRow);

      setCurrState((prev) => ({
        ...prev,
        selectedValue: firstRowValue,
        selectedData: firstRow,
      }));
    },
    /**
     * data binding
     * @param {*} source
     * @param {*} _setVal
     * @param {*} _callback
     */
    setDataBinding(source, _setVal = "", _callback) {
      _setDataBinding(source, _setVal, _callback);
    },
    /**
     * enable
     */
    enable() {
      setCurrState((prev) => ({
        ...prev,
        disabled: false,
      }));
    },
    /**
     * disable
     */
    disable() {
      setCurrState((prev) => ({
        ...prev,
        disabled: true,
      }));
    },
    async setSort(v) {
      _setSort(v);
    },

    updateState(stateName, newValue) {
      // currState에 있는 값만 업데이트 가능
      if (Object.keys(currState).includes(stateName)) {
        setCurrState((prev) => ({
          ...prev,
          [stateName]: newValue,
        }));
      } else {
        console.error(
          "E3Selectbox: State에 지정되지 않은 값을 업데이트 시도하였습니다. 잘못된 시도입니다."
        );
      }
    },
  }));

  // url이 지정된 경우, API를 통해 데이터 가져와 바인딩
  const _getDataFromAPIAndSet = async (
    url,
    method,
    body,
    _setVal = "",
    _callback
  ) => {
    const response = await request(
      url,
      { method: method ?? "GET", ...(body && url ? { body: body } : body) },
      { showConfirm: false, showLoad: false }
    );
    if (response) {
      _setDataBinding(
        url ? response : response.data[props.respName],
        _setVal,
        _callback
      );
    }
  };

  useEffect(() => {
    if (props.dataSource) {
      _setDataBinding(props.dataSource);
      return;
    }

    // Combobox 렌더링 후, 데이터 바인딩
    // 공통코드 데이터인 경우, LocalStorage에서 가져와 바인딩
    const getCommonCodesAndSet = async (parentCodeId) => {
      const codes = await getChildrenCommonCode(parentCodeId);
      if (codes?.length > 0) {
        _setDataBinding(codes);
      }
      return;
    };
    if (props.parentCodeId) {
      getCommonCodesAndSet(props.parentCodeId);
      return;
    }

    if (props.url || (!props.url && props.apiBody)) {
      _getDataFromAPIAndSet(props.url, props.apiMethod, props.apiBody);
    }
  }, []);

  const _setDataBinding = (source, _setVal = "", _callback) => {
    let data = source || [];
    if (props.sort) _sorting(source, props.sort);
    if (props.addAll) data = _addAll(data);
    if (props.addBlank) data = _addBlank(data);
    if (props.addText) data = _addText(data, props.addText);

    //BindingData labelId로 중복 제거
    if (props.removeDuplicateValue.length > 0) {
      data = _removeDup(data, props.removeDuplicateValue);
    }

    const defaultSelected = props.defaultValue
      ? data.findIndex(
          (x) =>
            String(x[props.valueId]).trim() ===
            String(props.defaultValue).trim()
        )
      : 0;

    const setVal = _setVal
      ? data.findIndex(
          (x) => String(x[props.valueId]).trim() === String(_setVal).trim()
        )
      : -1;

    const selectedIndex = setVal !== -1 ? setVal : defaultSelected;
    const selectedData = selectedIndex !== -1 ? data[selectedIndex] : null;
    const selectedValue = selectedData ? _keyValueForm(selectedData) : null;

    const options = _rtnOption(data);

    let multiBlankChk = false;
    if (
      props.isMulti &&
      selectedData?.[props.valueId] === "" &&
      selectedValue?.["value"] === ""
    ) {
      multiBlankChk = true;
    } else if (defaultSelected === -1) {
      multiBlankChk = true;
    }

    callbackRef.current = _callback ?? null;

    setCurrState((prev) => ({
      ...prev,
      selectedValue: multiBlankChk ? [] : selectedValue,
      selectedData: multiBlankChk ? [] : selectedData,
      dataSource: data,
      options: options,
    }));
  };

  const _rtnOption = (data = []) => {
    return data.map((item) => ({
      value: item[props.valueId],
      label: item[props.labelId],
    }));
  };

  useEffect(() => {
    if (callbackRef.current) {
      callbackRef.current({
        selectedValue: currState.selectedValue ?? null,
        selectedData: currState.selectedData ?? null,
      });
      callbackRef.current = null;
    }
  }, [currState.selectedValue, currState.selectedData]);

  const _removeDup = (data = [], key = "") => {
    const set = new Set();
    return data.filter((item) => {
      const target = item[key];
      if (set.has(target)) {
        return false;
      } else {
        set.add(target);
        return true;
      }
    });
  };

  const _addBlank = (data = []) => {
    const blank = {
      [props.valueId]: "",
      [props.labelId]: "",
      ord: 0,
    };

    return [blank, ...data];
  };

  const _addAll = (data = []) => {
    const all = {
      [props.valueId]: "",
      [props.labelId]: "전체",
      ord: 0,
    };

    return [all, ...data];
  };
  const _addText = (data = [], text) => {
    const blank = {
      [props.valueId]: "",
      [props.labelId]: text,
      ord: 0,
    };

    return [blank, ...data];
  };

  const _setValue = (e, action) => {
    const { value } = e || { value: null };
    const { dataSource } = currState;
    const found = dataSource?.find((data) => data[props.valueId] == value);

    if (props.isMulti) {
      const newData = _dataForm(currState.selectedValue, e, action);

      setCurrState((prev) => ({
        ...prev,
        selectedValue: newData,
        selectedData: found,
      }));
    } else {
      setCurrState((prev) => ({
        ...prev,
        selectedValue: e,
        selectedData: found,
      }));
    }

    if (props.onChange) {
      props.onChange(e, found);
    }

    if (event?.key === "Enter") {
      if (props.onEnterPress) {
        props.onEnterPress(event, e);
      }
    } else {
      if (props.onKeyUp) {
        props.onKeyUp(event, e);
      }
    }
  };

  const _keyValueForm = (param) => {
    if (!param) return {};

    return (
      param > "" && {
        value: param[props.valueId],
        label: param[props.labelId],
      }
    );
  };

  const _onChange = (e, action) => {
    _setValue(e, action);
  };

  const colourStyles = {
    menu: (provided) => ({ ...provided, zIndex: 999 }),
    option: (style) => ({ ...style, minHeight: 25 }),
    singleValue: (provided) => {
      return { ...provided, color: "#999" };
    },
    menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
  };

  const _dataForm = (selectedValue, eventValue, action) => {
    const newData = _rtnArrayData(selectedValue);
    const pushData = _rtnArrayData(eventValue);
    let rtnArr = [];

    pushData?.forEach((x) => {
      newData.push(x);
    });

    const _item = new Set(newData?.map(JSON.stringify));
    rtnArr = Array.from(_item).map(JSON.parse);

    // 단일, 전체삭제 로직( 단일 삭제 시 index, 전체 삭제 시 array = [])
    if (action?.action === "remove-value") {
      rtnArr = rtnArr?.filter((item) => {
        return JSON.stringify(item) !== JSON.stringify(action.removedValue);
      });
    } else if (action?.action === "clear") {
      rtnArr = [];
    }

    return rtnArr;
  };

  const _rtnArrayData = (str) => {
    if (str === "") {
      return [];
    } else if (Array.isArray(str)) {
      return str;
    } else {
      try {
        return [str];
      } catch (error) {
        return [];
      }
    }
  };

  /**
   * 키 입력 이벤트
   */
  const _onKeyDown = (e) => {
    if (props.onKeyDown) {
      props.onKeyDown(e);
    }
  };

  /**
   * sorting
   * @param {*} codes
   * @param {*} v
   * @returns
   */
  const _sorting = async (codes = [], v) => {
    if (!codes.length || !v?.field) return codes;

    const { field: key, order = "asc" } = v;
    const isValueNumeric = codes[0][key] !== "" && !isNaN(codes[0][key]);

    const compare = (a, b) => {
      const aValue = a[key];
      const bValue = b[key];

      if (isValueNumeric) {
        return aValue - bValue;
      } else {
        return aValue.localeCompare(bValue);
      }
    };

    codes.sort((a, b) => (order === "desc" ? compare(b, a) : compare(a, b)));

    return codes;
  };

  /**
   * sorting
   * @param {*} v
   */
  const _setSort = async (v) => {
    const { dataSource } = currState;
    const _data = _.cloneDeep(dataSource);

    _sorting(_data, v).then((codes) => {
      if (props.removeDuplicateValue.length > 0) {
        codes = _removeDup(codes, props.removeDuplicateValue);
      }
      const options = _rtnOption(codes);

      setCurrState((prev) => ({
        ...prev,
        dataSource: codes,
        options: options,
      }));
    });
  };

  return (
    <div
      className={`${props.controlWidth ?? ""} ${props.className ?? ""}`}
      style={{ width: "100%" }}
    >
      <Select
        className={`${styles.e3Select} ${styles.e3Select_x}`}
        styles={colourStyles}
        ref={refSelect}
        placeholder={currState.placeholder}
        isClearable={currState.isClearable}
        isSearchable={currState.isSearchable}
        onChange={_onChange}
        onKeyDown={_onKeyDown}
        isDisabled={currState.disabled}
        options={currState.options}
        value={currState.selectedValue}
        isMulti={props.isMulti}
        defaultValue={currState.selectedValue}
        menuPlacement={props.menuPlacement}
        defaultMenuIsOpen={props.defaultMenuIsOpen}
        {...(props.maxMenuHeight > -1 && {
          maxMenuHeight: props.maxMenuHeight,
        })}
        menuPortalTarget={document.body}
      />
    </div>
  );
});

const E3SelectboxPropTypes = {
  removeDuplicateValue: PropTypes.string,
  addBlank: PropTypes.bool,
  addAll: PropTypes.bool,
  valueId: PropTypes.string,
  labelId: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  onKeyDown: PropTypes.func,
  onEnterPress: PropTypes.func,
  onKeyUp: PropTypes.func,
  parentCodeId: PropTypes.string,
  url: PropTypes.string,
  apiMethod: PropTypes.string,
  apiBody: PropTypes.object,
  dataSource: PropTypes.array,
  controlWidth: PropTypes.string,
  defaultValue: PropTypes.string,
  isClearable: PropTypes.bool,
  isSearchable: PropTypes.bool,
  placeholder: PropTypes.string,
  isMulti: PropTypes.bool,
  defaultMenuIsOpen: PropTypes.bool,
  maxMenuHeight: PropTypes.number,
  respName: PropTypes.string,
  sort: PropTypes.object,
};

E3Selectbox.propTypes = E3SelectboxPropTypes;
E3Selectbox.defaultProps = {
  removeDuplicateValue: "",
  addBlank: false,
  addAll: false,
  valueId: "codeId",
  labelId: "codeName",
  disabled: false,
  onChange: null,
  onKeyDown: null,
  onEnterPress: null,
  onKeyUp: null,
  parentCodeId: null,
  url: null,
  apiMethod: null,
  apiBody: null,
  dataSource: null,
  controlWidth: null,
  defaultValue: "",
  isClearable: true,
  isSearchable: true,
  placeholder: "",
  isMulti: false,
  defaultMenuIsOpen: false,
  maxMenuHeight: -1,
  respName: "result",
  sort: null, // ex) { field: 'ord', order: 'asc' }
};
E3Selectbox.displayName = "E3Selectbox";
export default E3Selectbox;
